import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Copy,
  Trash2,
  ExternalLink,
  ChevronRight,
  ListChecks,
  Video,
  Loader2,
  Plus,
  Pencil,
  Check,
  X,
  Sparkles,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import ResumesSection from "@/components/ResumesSection";
import EmbedSnippet from "@/components/EmbedSnippet";
import SocialShareCard from "@/components/SocialShareCard";
import AIQuestionGeneratorModal from "@/components/AIQuestionGeneratorModal";
import ScorecardRubric from "@/components/ScorecardRubric";

export default function JobDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingLive, setStartingLive] = useState(false);

  const fetchAll = async () => {
    try {
      const [{ data: j }, { data: ix }] = await Promise.all([
        api.get(`/jobs/${jobId}`),
        api.get(`/jobs/${jobId}/interviews`),
      ]);
      setJob(j);
      setInterviews(ix);
    } catch (err) {
      toast.error("Could not load job");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const candidateLink = job
    ? `${window.location.origin}/interview/${job.id}`
    : "";

  const copyLink = () => {
    navigator.clipboard.writeText(candidateLink);
    toast.success("Candidate link copied");
  };

  const onDelete = async () => {
    await api.delete(`/jobs/${jobId}`);
    toast.success("Role deleted");
    navigate("/dashboard");
  };

  const [skipWarmup, setSkipWarmup] = useState(false);

  const startLive = async () => {
    setStartingLive(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/live-rooms`, {
        skip_warmup: skipWarmup,
      });
      navigate(`/jobs/${jobId}/live/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start live room");
    } finally {
      setStartingLive(false);
    }
  };

  // ---- Question editing ----
  const [editingQid, setEditingQid] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newText, setNewText] = useState("");
  const [questionBusy, setQuestionBusy] = useState(false);

  const beginEdit = (q) => {
    setEditingQid(q.id);
    setEditingText(q.text);
  };
  const cancelEdit = () => {
    setEditingQid(null);
    setEditingText("");
  };
  const saveEdit = async (q) => {
    const text = editingText.trim();
    if (!text) return;
    setQuestionBusy(true);
    try {
      await api.put(`/jobs/${jobId}/questions/${q.id}`, {
        text,
        time_limit_seconds: q.time_limit_seconds || 120,
      });
      setJob({
        ...job,
        questions: job.questions.map((x) =>
          x.id === q.id ? { ...x, text } : x
        ),
      });
      toast.success("Question updated");
      cancelEdit();
    } catch {
      toast.error("Could not update question");
    } finally {
      setQuestionBusy(false);
    }
  };
  const deleteQuestion = async (q) => {
    setQuestionBusy(true);
    try {
      await api.delete(`/jobs/${jobId}/questions/${q.id}`);
      setJob({
        ...job,
        questions: job.questions.filter((x) => x.id !== q.id),
      });
      toast.success("Question removed");
    } catch {
      toast.error("Could not delete question");
    } finally {
      setQuestionBusy(false);
    }
  };
  const addQuestion = async () => {
    const text = newText.trim();
    if (!text) return;
    setQuestionBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/questions`, {
        text,
        time_limit_seconds: 120,
      });
      setJob({ ...job, questions: [...job.questions, data] });
      setNewText("");
      setAddingNew(false);
      toast.success("Question added");
    } catch {
      toast.error("Could not add question");
    } finally {
      setQuestionBusy(false);
    }
  };

  const [regeneratingQid, setRegeneratingQid] = useState(null);

  /**
   * Bulk-append AI-generated questions from the modal. Each item: {text, category}.
   * Fires one POST per question (the existing endpoint is per-question; this
   * is acceptable for up to 8 questions and keeps server.py unchanged).
   * Optimistic UI: success toast lives in the modal; we just refresh the job
   * state at the end so failed adds don't leave phantom rows.
   */
  const addAiQuestions = async (questions) => {
    const added = [];
    for (const q of questions) {
      try {
        const { data } = await api.post(`/jobs/${jobId}/questions`, {
          text: q.text,
          time_limit_seconds: 120,
          category: q.category,
          listen_for: q.listen_for,
        });
        added.push(data);
      } catch (err) {
        // Swallow per-item failures — surface a single toast at the end
        // if at least one failed but at least one succeeded.
        console.warn("AI question add failed:", err);
      }
    }
    if (added.length) {
      setJob((prev) =>
        prev ? { ...prev, questions: [...prev.questions, ...added] } : prev
      );
    }
    if (added.length < questions.length) {
      toast.warning(
        `Added ${added.length} of ${questions.length} — try again for the rest.`
      );
    }
  };

  const regenerateQuestion = async (q) => {
    setRegeneratingQid(q.id);
    try {
      const { data } = await api.post(
        `/jobs/${jobId}/questions/${q.id}/regenerate`
      );
      setJob({
        ...job,
        questions: job.questions.map((x) =>
          x.id === q.id ? { ...x, text: data.text } : x
        ),
      });
      toast.success("Question regenerated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate");
    } finally {
      setRegeneratingQid(null);
    }
  };

  if (loading || !job) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-12 text-slate-500">
        Loading…
      </main>
    );
  }

  return (
    <main data-testid="job-detail-page" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-12">
      <button
        data-testid="job-detail-back-btn"
        onClick={() => navigate("/dashboard")}
        className="mb-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-3 w-3" /> Dashboard
      </button>

      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
            Role
          </div>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            {job.title}
          </h1>
          {job.description && (
            <p className="mt-3 max-w-2xl text-sm text-slate-500">{job.description}</p>
          )}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              data-testid="job-delete-btn"
              className="rounded-sm border-red-200 bg-red-50 text-red-600 hover:bg-red-100"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Job Title
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this role?</AlertDialogTitle>
              <AlertDialogDescription>
                All associated candidate interviews will be removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="job-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="job-delete-confirm"
                onClick={onDelete}
                className="bg-red-600 hover:bg-red-500"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="mt-10 grid gap-px bg-slate-200 lg:grid-cols-12">
        {/* Candidate link */}
        <section className="bg-white p-8 lg:col-span-7">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
            Candidate invite link
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Input
              data-testid="candidate-link-input"
              value={candidateLink}
              readOnly
              className="flex-1 rounded-sm border-slate-300 font-mono text-sm"
            />
            <Button
              data-testid="copy-candidate-link-btn"
              onClick={copyLink}
              variant="outline"
              className="rounded-sm border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy
            </Button>
            <a href={candidateLink} target="_blank" rel="noreferrer">
              <Button
                data-testid="open-candidate-link-btn"
                variant="outline"
                className="rounded-sm border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open
              </Button>
            </a>
          </div>
          <p className="mt-3 text-xs text-slate-900">
            Share this link with candidates. They'll record video answers directly
            in the browser — no accounts required.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-5">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
                Or — go live now
              </div>
              <p className="mt-1 text-xs text-slate-900">
                Spin up a 1:1 camera-to-camera room and share the link with the candidate.
              </p>
              <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700">
                <input
                  type="checkbox"
                  data-testid="start-live-skip-warmup-toggle"
                  checked={skipWarmup}
                  onChange={(e) => setSkipWarmup(e.target.checked)}
                  className="h-3.5 w-3.5 rounded-sm border-slate-300 text-emerald-600"
                />
                Skip the 60s warm-up · go straight to the live call
              </label>
            </div>
            <Button
              data-testid="start-live-interview-btn"
              onClick={startLive}
              disabled={startingLive}
              className="rounded-sm bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {startingLive ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Video className="mr-2 h-4 w-4" />
              )}
              Start live interview
            </Button>
          </div>

          {/* Per-role embed snippet */}
          {user?.id && (
            <div className="mt-6 border-t border-slate-200 pt-5">
              <EmbedSnippet
                title="Embed this role on your site"
                description={`Drop the iframe below into any blog post, careers page, or LinkedIn job page to let visitors apply directly to "${job.title}". Each résumé is AI-scored against this role automatically.`}
                publicUrl={`${window.location.origin}/c/${user.id}/j/${job.id}`}
                iframeTitle={`Apply for ${job.title}`}
                height={760}
                testIdPrefix="job-embed"
              />
              <div className="mt-4">
                <SocialShareCard
                  jobTitle={job.title}
                  jobDescription={job.description}
                  cardImageUrl={`${process.env.REACT_APP_BACKEND_URL}/api/public/careers/${user.id}/jobs/${job.id}/card.png`}
                  publicApplyUrl={`${window.location.origin}/c/${user.id}/j/${job.id}`}
                  shareUrl={`${process.env.REACT_APP_BACKEND_URL}/api/share/c/${user.id}/j/${job.id}`}
                  companyLabel={user.company || user.name || "team"}
                />
              </div>
            </div>
          )}
        </section>

        {/* Questions */}
        <section className="bg-white p-8 lg:col-span-5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
              <ListChecks className="h-3 w-3" /> Interview questions
            </div>
            {!addingNew && (
              <div className="flex items-center gap-2">
                <AIQuestionGeneratorModal
                  jobId={jobId}
                  onAddQuestions={addAiQuestions}
                />
                <Button
                  data-testid="add-question-btn"
                  size="sm"
                  onClick={() => setAddingNew(true)}
                  className="h-7 rounded-sm bg-blue-600 text-xs text-white hover:bg-blue-700"
                >
                  <Plus className="mr-1 h-3 w-3" /> Add Interview Questions
                </Button>
              </div>
            )}
          </div>

          <ol className="mt-4 space-y-3">
            {job.questions.map((q, i) => {
              const isEditing = editingQid === q.id;
              return (
                <li
                  key={q.id}
                  data-testid={`question-${i}`}
                  className="border-b border-slate-100 pb-3 text-sm last:border-0"
                >
                  <div className="flex items-start gap-3">
                    <span className="mt-0.5 font-mono text-blue-700">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {isEditing ? (
                      <div className="flex flex-1 items-start gap-2">
                        <Input
                          autoFocus
                          data-testid={`edit-question-input-${i}`}
                          value={editingText}
                          onChange={(e) => setEditingText(e.target.value)}
                          className="flex-1 rounded-sm border-slate-300 text-sm"
                        />
                        <Button
                          data-testid={`save-question-btn-${i}`}
                          size="icon"
                          variant="outline"
                          disabled={questionBusy}
                          onClick={() => saveEdit(q)}
                          className="h-9 w-9 rounded-sm border-slate-300"
                        >
                          <Check className="h-4 w-4 text-emerald-600" />
                        </Button>
                        <Button
                          data-testid={`cancel-question-btn-${i}`}
                          size="icon"
                          variant="outline"
                          onClick={cancelEdit}
                          className="h-9 w-9 rounded-sm border-slate-300"
                        >
                          <X className="h-4 w-4 text-slate-500" />
                        </Button>
                      </div>
                    ) : (
                      <span className="flex-1">{q.text}</span>
                    )}
                  </div>
                  {!isEditing && q.listen_for && (
                    <p
                      data-testid={`question-listenfor-${i}`}
                      className="ml-7 mt-2 border-l-2 border-amber-300 bg-amber-50/50 pl-3 text-[11px] italic leading-relaxed text-slate-700"
                    >
                      <strong className="not-italic text-amber-700">
                        Listen for:
                      </strong>{" "}
                      {q.listen_for}
                    </p>
                  )}
                  {!isEditing && (
                    <div className="mt-2 flex flex-wrap gap-1 pl-7">
                      <button
                        data-testid={`regen-question-btn-${i}`}
                        onClick={() => regenerateQuestion(q)}
                        disabled={regeneratingQid === q.id}
                        aria-label="Regenerate"
                        className="inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-50"
                      >
                        {regeneratingQid === q.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Sparkles className="h-3 w-3" />
                        )}
                        Regenerate
                      </button>
                      <button
                        data-testid={`edit-question-btn-${i}`}
                        onClick={() => beginEdit(q)}
                        aria-label="Edit"
                        className="inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                      >
                        <Pencil className="h-3 w-3" />
                        Edit
                      </button>
                      <button
                        data-testid={`delete-question-btn-${i}`}
                        onClick={() => deleteQuestion(q)}
                        disabled={questionBusy}
                        aria-label="Delete"
                        className="inline-flex items-center gap-1 rounded-sm border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="h-3 w-3" />
                        Delete
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
            {addingNew && (
              <li
                data-testid="add-question-row"
                className="flex items-start gap-3 border-t-2 border-blue-100 pt-3 text-sm"
              >
                <span className="mt-0.5 font-mono text-blue-700">+</span>
                <Input
                  autoFocus
                  data-testid="new-question-input"
                  placeholder="Type your question…"
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  className="flex-1 rounded-sm border-slate-300 text-sm"
                />
                <Button
                  data-testid="save-new-question-btn"
                  size="icon"
                  variant="outline"
                  disabled={questionBusy || !newText.trim()}
                  onClick={addQuestion}
                  className="h-9 w-9 rounded-sm border-slate-300"
                >
                  <Check className="h-4 w-4 text-emerald-600" />
                </Button>
                <Button
                  data-testid="cancel-new-question-btn"
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    setAddingNew(false);
                    setNewText("");
                  }}
                  className="h-9 w-9 rounded-sm border-slate-300"
                >
                  <X className="h-4 w-4 text-slate-500" />
                </Button>
              </li>
            )}
            {job.questions.length === 0 && !addingNew && (
              <li
                data-testid="no-questions"
                className="border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500"
              >
                No questions yet — click <strong>Add</strong> to create one.
              </li>
            )}
          </ol>
        </section>

        {/* Per-job scorecard rubric — sliders to weight categories */}
        <div className="bg-white lg:col-span-12">
          <ScorecardRubric
            jobId={jobId}
            initialWeights={job.category_weights}
          />
        </div>
      </div>

      {/* Resumes */}
      <ResumesSection jobId={jobId} jobTitle={job?.title} />

      {/* Candidates */}
      <section className="mt-12">
        <h2 className="font-heading text-xl font-medium">Candidates</h2>
        <div className="mt-4 border border-slate-200">
          {interviews.length === 0 ? (
            <div
              data-testid="no-candidates-empty"
              className="p-12 text-center text-sm text-slate-500"
            >
              No candidates yet. Share your invite link to start collecting
              interviews.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Candidate
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Status
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Score
                  </TableHead>
                  <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-900">
                    Started
                  </TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {interviews.map((iv) => (
                  <TableRow
                    key={iv.id}
                    data-testid={`candidate-row-${iv.id}`}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/jobs/${jobId}/candidates/${iv.id}`)}
                  >
                    <TableCell>
                      <div className="font-medium">{iv.candidate_name}</div>
                      <div className="text-xs text-slate-500">{iv.candidate_email}</div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${
                          iv.status === "completed"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-amber-50 text-amber-600"
                        }`}
                      >
                        {iv.status === "completed" ? "Completed" : "In progress"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {iv.overall_score != null ? (
                        <span className="font-heading text-lg font-semibold">
                          {iv.overall_score}
                          <span className="text-xs text-slate-400">/10</span>
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(iv.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </section>
    </main>
  );
}
